import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill out all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      toast.success("Message sent. We'll reply within 1–2 business days.");
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not send. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="bg-[#F7F7F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="overline">— Contact</div>
            <h2 className="mt-5 font-sora text-[36px] sm:text-[44px] lg:text-[52px] font-bold text-[#111111] tracking-tight leading-[1.05]">
              Say hello.
            </h2>
            <p className="mt-6 font-manrope text-[16px] md:text-[17px] text-[#111111]/65 leading-relaxed max-w-md">
              Questions, partnerships, or a custom kit request? Send us a note
              — every message lands in a real inbox.
            </p>

            <a
              href="mailto:hello@vaymilo.com"
              data-testid="contact-email-link"
              className="mt-8 inline-flex items-center gap-2 font-sora text-[20px] md:text-[22px] font-semibold text-[#111111] hover:text-[#2563EB] transition-colors"
            >
              hello@vaymilo.com
              <ArrowUpRight size={18} strokeWidth={2.25} />
            </a>

            <div className="mt-10 grid grid-cols-2 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-xl overflow-hidden max-w-sm">
              <div className="bg-white px-5 py-4">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/45">
                  Reply
                </div>
                <div className="mt-1 font-manrope text-sm text-[#111111]">
                  1–2 business days
                </div>
              </div>
              <div className="bg-white px-5 py-4">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/45">
                  Hours
                </div>
                <div className="mt-1 font-manrope text-sm text-[#111111]">
                  Mon — Fri
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-10"
            >
              {/* Honeypot — hidden from humans, visible to bots */}
              <div className="vaymilo-honeypot" aria-hidden="true">
                <label htmlFor="contact-website">Website (leave empty)</label>
                <input
                  id="contact-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                  data-testid="honeypot-contact"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block font-manrope text-sm font-medium text-[#111111] mb-2"
                  >
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="input-contact-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder="Your name"
                    className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block font-manrope text-sm font-medium text-[#111111] mb-2"
                  >
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="input-contact-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    placeholder="you@domain.com"
                    className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="contact-message"
                  className="block font-manrope text-sm font-medium text-[#111111] mb-2"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="input-contact-message"
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={6}
                  placeholder="Tell us what you're working on…"
                  className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all resize-none"
                />
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="font-manrope text-[13px] text-[#111111]/55">
                  We&apos;ll only use your email to reply.
                </p>
                <button
                  type="submit"
                  data-testid="btn-contact-submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-manrope font-medium text-[15px] px-6 py-3 rounded-lg transition-colors"
                >
                  {loading ? "Sending…" : "Send message"}
                  {!loading && <ArrowUpRight size={16} strokeWidth={2.25} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
